import React, { useCallback } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { useTicketFetcher } from 'flow/hooks/useTicketFetcher';
import {
    selectTicketIsFetched,
    selectTicketState,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';

import BaseListItem, {
    rowMarking,
} from 'flow/components/ui-components/BaseListItem';
import ChannelIcon from 'flow/components/tickets/convenience/ChannelIcon';
import SectionChips from './sections/SectionChips';
import SectionHeader from './sections/SectionHeader';
import SectionQueueLine from './sections/SectionQueueLine';
import SectionSubject from './sections/SectionSubject';
import { TicketsListItemSkeleton } from './TicketsListSkeleton';

interface Props {
    isSelected: boolean;
    onClick: (key: TicketKey) => void;
    ticketKey: TicketKey;
}

const TicketListItem = ({ isSelected, onClick, ticketKey }: Props) => {
    const ticketIsFetched = useAppSelector((state) =>
        selectTicketIsFetched(state, ticketKey),
    );
    // Closed tickets render fully de-emphasised (mid-gray throughout).
    const muted = useAppSelector(
        (state) => selectTicketState(state, ticketKey) === 'closed',
    );

    useTicketFetcher({ ticketKey, ticketIsFetched });

    const onClickListItem = useCallback(() => {
        onClick(ticketKey);
    }, [onClick, ticketKey]);

    if (!ticketIsFetched) return <TicketsListItemSkeleton />;

    return (
        <BaseListItem
            onClick={onClickListItem}
            marking={rowMarking({ pageOpen: isSelected })}
        >
            <div className="flex w-full items-start gap-x-2">
                <div className="flex h-6 items-center">
                    <ChannelIcon
                        ticketKey={ticketKey}
                        size="medium"
                        outlined={true}
                        muted={muted}
                    />
                </div>
                <div className="flex min-w-0 grow flex-col">
                    <SectionHeader
                        ticketKey={ticketKey}
                        isSelected={isSelected}
                        muted={muted}
                    />
                    <SectionSubject ticketKey={ticketKey} muted={muted} />
                    <SectionQueueLine ticketKey={ticketKey} muted={muted} />
                    <SectionChips
                        ticketKey={ticketKey}
                        isSelected={isSelected}
                        muted={muted}
                    />
                </div>
            </div>
        </BaseListItem>
    );
};

export default React.memo(TicketListItem);
